import { Link } from "react-router-dom";
import { ArrowRight, Scissors, Trees, Sprout, Shovel, CheckCircle2, Star, Quote, Leaf } from "lucide-react";

const services = [
  {
    icon: Scissors,
    title: "Hækklipning",
    desc: "Præcis klipning af hække i alle højder — med bortkørsel og oprydning.",
    to: "/haekklipning",
  },
  {
    icon: Trees,
    title: "Beskæring",
    desc: "Formklipning og beskæring af træer og buske gennem hele sæsonen.",
    to: "/havearbejde",
  },
  {
    icon: Sprout,
    title: "Havepleje",
    desc: "Græsklip, lugning, plantning og vedligehold — vi passer haven for dig.",
    to: "/havearbejde",
  },
  {
    icon: Shovel,
    title: "Anlæg",
    desc: "Nye bede, fliser og små haveprojekter — udført med håndværk og omtanke.",
    to: "/havearbejde",
  },
];

const testimonials = [
  {
    name: "Anne M. Larsen",
    location: "Sønderborg",
    text: "Fantastisk arbejde med vores 40 meter lange bøgehæk. Kom til aftalt tid, klippede smukt og efterlod haven ren.",
    rating: 5,
  },
  {
    name: "Peter Nielsen",
    location: "Aabenraa",
    text: "Rimelig pris, ærlig kommunikation og et flot resultat. Jeg har allerede booket dem til næste år.",
    rating: 5,
  },
  {
    name: "Mette Sørensen",
    location: "Gråsten",
    text: "Endelig et havefirma man kan stole på. De ved præcis hvad de laver, og prisen holdt.",
    rating: 5,
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-soft border border-brand-border px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-brand-muted">
                <Leaf className="h-3.5 w-3.5 text-brand-primary" /> Havepleje i Syddanmark
              </div>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-brand-text text-balance">
                En velplejet have <span className="italic text-brand-primary">uden</span> besværet.
              </h1>
              <p className="mt-6 text-lg text-brand-muted max-w-xl leading-relaxed">
                Syddansk Group klipper hække og passer haver med præcision, respekt for naturen og fair priser. Fast pris fra <strong className="text-brand-text">32 kr/m</strong> — inkl. bortkørsel og oprydning.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/kontakt"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-primary text-brand-bg px-7 py-3.5 font-medium hover:bg-brand-hover transition-transform hover:-translate-y-0.5"
                >
                  Få et tilbud <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/priser"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-brand-primary text-brand-primary px-7 py-3.5 font-medium hover:bg-brand-primary/5 transition-colors"
                >
                  Beregn din pris
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-brand-muted">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-primary" /> Fast pris, ingen skjulte gebyrer
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-primary" /> Fuldt forsikret
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-primary" /> 7+ års erfaring
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div className="relative rounded-[2.5rem] overflow-hidden aspect-[4/5] lg:aspect-[5/6] border border-brand-border">
                <img
                  src="https://images.pexels.com/photos/32427439/pexels-photo-32427439.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="Hækklipning i haven"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/40 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 bg-brand-bg/95 backdrop-blur-md rounded-2xl p-5 border border-brand-border/60">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-brand-accent text-brand-accent" />
                    ))}
                    <span className="ml-2 text-sm font-medium text-brand-text">4.9 / 5</span>
                  </div>
                  <p className="text-sm text-brand-muted mt-1">Baseret på 120+ tilfredse kunder i Syddanmark</p>
                </div>
              </div>
              <div className="hidden lg:block absolute -top-6 -left-6 bg-brand-primary text-brand-bg rounded-2xl p-6 shadow-[0_20px_50px_-15px_rgba(42,77,59,0.5)] max-w-[220px]">
                <div className="font-display text-4xl">32 kr</div>
                <div className="text-sm text-brand-bg/80 mt-1">pr. meter hæk — inkl. bortkørsel</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">Vores ydelser</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text">
              Alt fra hæk til <span className="italic">helhedspleje</span>.
            </h2>
          </div>
          <p className="text-brand-muted max-w-md">
            Vi tilbyder et komplet program af havepleje — fra den ugentlige græsklip til det store anlægsprojekt.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <Link
              to={s.to}
              key={s.title}
              data-testid={`service-card-${i}`}
              className="group bg-brand-surface rounded-3xl border border-brand-border p-7 hover:shadow-[0_20px_40px_-15px_rgba(42,77,59,0.15)] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="h-12 w-12 rounded-2xl bg-brand-soft grid place-items-center mb-6 group-hover:bg-brand-primary group-hover:text-brand-bg transition-colors">
                <s.icon className="h-5 w-5" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-xl text-brand-text">{s.title}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{s.desc}</p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-brand-primary">
                Læs mere <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BENTO / WHY US */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid gap-4 md:grid-cols-6 md:grid-rows-2 md:h-[560px]">
          <div className="md:col-span-4 md:row-span-2 rounded-3xl overflow-hidden relative group">
            <img
              src="https://images.pexels.com/photos/2286895/pexels-photo-2286895.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Velplejet have"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-brand-bg">
              <div className="text-xs uppercase tracking-[0.22em] opacity-80">Håndværk & omtanke</div>
              <h3 className="font-display text-3xl sm:text-4xl mt-2 max-w-lg">
                Vi behandler din have som var den vores egen.
              </h3>
            </div>
          </div>
          <div className="md:col-span-2 rounded-3xl bg-brand-primary text-brand-bg p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-brand-bg/70">Erfaring</div>
              <div className="font-display text-6xl mt-3">7+</div>
              <div className="text-sm text-brand-bg/80 mt-1">års erfaring i branchen</div>
            </div>
            <Leaf className="h-8 w-8 text-brand-bg/50" />
          </div>
          <div className="md:col-span-2 rounded-3xl bg-brand-surface border border-brand-border p-8 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-brand-muted">Tilfredshed</div>
              <div className="font-display text-6xl mt-3 text-brand-primary">98%</div>
              <div className="text-sm text-brand-muted mt-1">af kunderne booker igen næste sæson</div>
            </div>
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-brand-accent text-brand-accent" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-muted mb-3">Kunder fortæller</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-text">
            Bag hver hæk står en <span className="italic">tilfreds nabo</span>.
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <div
              key={i}
              data-testid={`testimonial-${i}`}
              className="bg-brand-surface rounded-3xl border border-brand-border p-8"
            >
              <Quote className="h-6 w-6 text-brand-primary/60" />
              <p className="mt-4 text-brand-text leading-relaxed">&ldquo;{t.text}&rdquo;</p>
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm text-brand-text">{t.name}</div>
                  <div className="text-xs text-brand-muted">{t.location}</div>
                </div>
                <div className="flex">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-brand-accent text-brand-accent" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-8">
        <div className="rounded-[2.5rem] bg-brand-primary text-brand-bg p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-bg/5" />
          <div className="absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-brand-bg/5" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight">
                Klar til en velplejet have?
              </h2>
              <p className="mt-4 text-brand-bg/80 max-w-lg">
                Beregn din pris på under 30 sekunder — eller ring til os for en uforpligtende snak.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/priser"
                data-testid="cta-strip-calc"
                className="inline-flex items-center gap-2 rounded-full bg-brand-bg text-brand-primary px-7 py-3.5 font-medium hover:bg-brand-soft"
              >
                Beregn pris <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/kontakt"
                data-testid="cta-strip-contact"
                className="inline-flex items-center gap-2 rounded-full border border-brand-bg/40 text-brand-bg px-7 py-3.5 font-medium hover:bg-brand-bg/10"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
